import * as React from 'react';
import { Theme, makeStyles, createStyles } from '@bb-ui/react-library';
import clsx from 'clsx';
import { matchPath, Link, useLocation } from 'react-router-dom';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { useTranslation } from 'react-i18next';
import { AppRouteProps } from 'routes/AppRoute';
import { useAppRouteContext } from 'contexts/AppRouteContext';
import { useTenantContext } from 'contexts/TenantContext';

export interface BackNavigationProps {
  className?: string;
  replaceText?: { [key: string]: string };
}

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      fontSize: '13px',
      margin: '0 0 4px 0',
      display: 'inline-flex',
      alignItems: 'center',
      alignSelf: 'flex-start',
      textDecoration: 'none',
      color: 'rgba(0, 0, 0, 0.50)',
      fontWeight: 500,
      transition: 'color 0.15s ease',
      '&:hover': {
        color: 'rgba(0, 0, 0, 0.87)',
      },
    },
    arrow: {
      marginRight: theme.spacing(0.75),
      fontSize: '14px',
    },
  }),
);

export const BackNavigation: React.FunctionComponent<BackNavigationProps> = (props) => {
  const { replaceText, className } = props;
  const { allRoutes } = useAppRouteContext();
  const { tenant, parentTenant } = useTenantContext();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const classes = useStyles(props);

  // Recursively search for the deepest route config matching the current
  // pathname. If there is more than one matching route config at a specific
  // level, we choose the longest path. We need to consider every route config
  // because we will match on a parent route before its children.

  function routeForPath(path: string, routes: AppRouteProps[]) {
    let result: AppRouteProps | undefined;
    let resultSpecificity = -1;

    function search(routes: AppRouteProps[], currentSpecificity = 0): void {
      routes.forEach((route) => {
        if (
          matchPath(path, { exact: route.exact, path: route.path }) &&
          (!result ||
            currentSpecificity > resultSpecificity ||
            (currentSpecificity === resultSpecificity && result.path.length < route.path.length))
        ) {
          result = route;
          resultSpecificity = currentSpecificity;
        }

        // Check nested routes.

        if (route.routes) {
          search(route.routes, currentSpecificity + 1);
        }
      });
    }

    search(routes);
    return result;
  }

  function popPath(path: string) {
    return path.replace(/\/[^/]*$/, '');
  }

  let path = pathname;
  const breadcrumbs: { path: string; name: string }[] = [];

  while (path.indexOf('/') !== -1) {
    const route = routeForPath(path, allRoutes);

    if (route && route.name) {
      breadcrumbs.unshift({
        path,
        name: (replaceText && replaceText[route.name]) || t(route.name),
      });
    }

    path = popPath(path);
  }

  const parentId = tenant?.parentTenant;
  const tenantType = tenant?.tenantType;

  if (parentId && tenantType) {
    if (tenantType === 'Learn') {
      breadcrumbs.unshift({ path: `/client/${parentId}`, name: `${parentTenant?.name}` });
      breadcrumbs.unshift({ path: `/client`, name: 'Learn Clients' });
      breadcrumbs[2].path = `/client/${parentId}/assets`;
      breadcrumbs[2].name = 'Assets';
    }
  }

  // If there is only one breadcrumb or no breadcrumbs, show nothing.

  if (breadcrumbs.length < 2) {
    return null;
  }

  // Get the parent (second to last breadcrumb)
  const parentBreadcrumb = breadcrumbs[breadcrumbs.length - 2];

  return (
    <Link
      to={parentBreadcrumb.path}
      className={clsx(classes.root, className)}
      data-testid="back-navigation"
    >
      <span className={classes.arrow}>←</span>
      <Typography variant="inherit">{parentBreadcrumb.name}</Typography>
    </Link>
  );
};

export default BackNavigation;
